import React, { useEffect, useState, useCallback } from 'react';
import './../Styles/Admin.css';
import logoLight from './../Images/tsqs-hm-ft-logo1.gif';
import logoDark from './../Images/tsqs-hm-ft-logo.gif';
import axios from 'axios';
import Lottie from 'lottie-react';
import comingSoonAnimation from './../ani/comingsoon.json';
import Notifications from './../Components/Notifications';
import EmployeeDetailsDialog from './EmployeeDetailsDialog';
import MESection from './MESection';
import Organization from './Organization';
import ProfileDialog from './ProfileDialog';
import AppHeader from './AppHeader';
import EmployeeDirectoryTable from './EmployeeDirectoryTable';
import ReviewedReports from './Reports/ReviewedReports';

const apiUrl = process.env.REACT_APP_API_URL;

const MainManager = () => {
    const [employees, setEmployees] = useState([]);
    const [managerInfo, setManagerInfo] = useState({ full_name: '', job_title: '' });
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedQuarter, setSelectedQuarter] = useState(null);
    const [isProfileDialogOpen, setProfileDialogOpen] = useState(false);

    const VALID_MAIN_MANAGER_SECTIONS = ['employees', 'me', 'organization', 'reports', 'projects', 'updates'];
    const [currentSection, setCurrentSection] = useState(() => {
        const savedSection = localStorage.getItem('mainManager_currentSection');
        return VALID_MAIN_MANAGER_SECTIONS.includes(savedSection) ? savedSection : 'employees';
    });

    useEffect(() => {
        if (VALID_MAIN_MANAGER_SECTIONS.includes(currentSection)) {
            localStorage.setItem('mainManager_currentSection', currentSection);
        }
    }, [currentSection]);

    const managerId = localStorage.getItem('employeeId');
    const [unreadUpdatesCount, setUnreadUpdatesCount] = useState(0);

    const fetchUnreadCount = useCallback(() => {
        if (!managerId) return;
        axios.get(`${apiUrl}/api/notifications/unread/${managerId}?isMainManager=1`)
            .then(res => setUnreadUpdatesCount(res.data?.unreadCount || 0))
            .catch(err => console.error('Error fetching unread count:', err));
    }, [managerId]);

    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 15000);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    const fetchEmployees = () => {
        if (!managerId) return;
        axios.get(`${apiUrl}/api/users`)
            .then(response => {
                const filteredUsers = (response.data || []).filter(u => u.employee_number !== managerId);
                setEmployees(filteredUsers);
            })
            .catch(error => console.error('Error fetching employee data:', error));
    };

    useEffect(() => {
        if (!managerId) {
            console.error('Manager ID not found in local storage.');
            return;
        }

        axios.get(`${apiUrl}/api/manager-info/${managerId}`)
            .then(response => {
                setManagerInfo(response.data);
                fetchEmployees();
            })
            .catch(error => console.error('Error fetching manager info:', error));
    }, [managerId]);

    useEffect(() => {
        if (currentSection === 'employees') {
            fetchEmployees();
        }
    }, [currentSection]);

    const handleOpenProfileDialog = () => {
        setProfileDialogOpen(true);
    };

    const handleCloseProfileDialog = () => {
        setProfileDialogOpen(false);
    };

    return (
        <div className="admin-page">
            <aside className="sidebar">
                <div className="logo-container">
                    <img src={logoLight} alt="Company Logo" className="logo logo-light" />
                    <img src={logoDark} alt="Company Logo" className="logo logo-dark" />
                </div>
                <nav className="nav-menu">
                    <a
                        href="#"
                        className={`nav-item ${currentSection === 'employees' ? 'active' : ''}`}
                        onClick={(e) => { e.preventDefault(); setCurrentSection('employees'); }}
                    >
                        Employees
                    </a>
                    <a
                        href="#"
                        className={`nav-item ${currentSection === 'me' ? 'active' : ''}`}
                        onClick={(e) => { e.preventDefault(); setCurrentSection('me'); }}
                    >
                        Manager View
                    </a>
                    <a
                        href="#"
                        className={`nav-item ${currentSection === 'organization' ? 'active' : ''}`}
                        onClick={(e) => { e.preventDefault(); setCurrentSection('organization'); }}
                    >
                        Organization
                    </a>
                    <a
                        href="#"
                        className={`nav-item ${currentSection === 'reports' ? 'active' : ''}`}
                        onClick={(e) => { e.preventDefault(); setCurrentSection('reports'); }}
                    >
                        Reviewed Reports
                    </a>
                    <a
                        href="#"
                        className={`nav-item ${currentSection === 'projects' ? 'active' : ''}`}
                        onClick={(e) => { e.preventDefault(); setCurrentSection('projects'); }}
                    >
                        Project Reports
                    </a>
                    <a
                        href="#"
                        className={`nav-item ${currentSection === 'updates' ? 'active' : ''}`}
                        onClick={(e) => { e.preventDefault(); setCurrentSection('updates'); }}
                    >
                        <div className="nav-item-content">
                            <span>Updates</span>
                            {unreadUpdatesCount > 0 && (
                                <span className="sidebar-unread-pill" title={`${unreadUpdatesCount} unread update${unreadUpdatesCount > 1 ? 's' : ''}`}>
                                    {unreadUpdatesCount > 99 ? '99+' : unreadUpdatesCount}
                                </span>
                            )}
                        </div>
                    </a>
                </nav>
            </aside>

            <main className="main-content">
                <AppHeader
                    title="PERFORMANCE MANAGEMENT SYSTEM"
                    employeeInfo={managerInfo}
                    onProfileClick={handleOpenProfileDialog}
                    hideLogo={true}
                />

                {currentSection !== 'me' && currentSection !== 'organization' && currentSection !== 'reports' && (
                    <div className="welcome-section">
                        <h1>Hi {managerInfo.full_name}...</h1>
                    </div>
                )}

                {currentSection === 'employees' && (
                    <EmployeeDirectoryTable
                        title="List of Employees"
                        employees={employees}
                        onViewDetails={(emp) => setSelectedEmployee(emp)}
                        showGoalActions={true}
                        showProjectFilter={true}
                        showRoleFilter={true}
                        availableRoles={['all', 'admin', 'lead', 'employee']}
                        loggedInManagerId={managerId}
                        isMainManagerFlag={1}
                    />
                )}

                {currentSection === 'me' && (
                    <MESection
                        managerId={managerId}
                        managerName={managerInfo.full_name ? managerInfo.full_name.split(' ')[0] : 'Manager'}
                        onEmployeeAdded={fetchEmployees}
                        onEmployeeDeleted={fetchEmployees}
                    />
                )}

                {currentSection === 'reports' && (
                    <div className="reports-section-wrapper">
                        <ReviewedReports
                            onViewDetails={(emp, quarter) => {
                                setSelectedEmployee(emp);
                                setSelectedQuarter(quarter);
                            }}
                        />
                    </div>
                )}

                {currentSection === 'projects' && (
                    <div>
                        Projects COMING SOON...
                        <Lottie animationData={comingSoonAnimation} className="card-animation" />
                    </div>
                )}

                {currentSection === 'updates' && (
                    <div className="updates-section">
                        <Notifications
                            managerId={managerId}
                            isMainManager={true}
                            onUnreadCountChange={setUnreadUpdatesCount}
                        />
                    </div>
                )}

                {currentSection === 'organization' && (
                    <div className="organization-section-wrapper">
                        <Organization
                            managerName={managerInfo.full_name ? managerInfo.full_name.split(' ')[0] : 'Manager'}
                            usersEndpoint={`${apiUrl}/api/users`}
                            isMainManager={true}
                            allowEdit={true}
                        />
                    </div>
                )}
            </main>

            {selectedEmployee && (
                <EmployeeDetailsDialog
                    employee={selectedEmployee}
                    selectedQuarter={selectedQuarter}
                    reviewerRole="main-manager"
                    readOnly={currentSection === 'reports' || Boolean(selectedQuarter)}
                    onClose={() => {
                        setSelectedEmployee(null);
                        setSelectedQuarter(null);
                    }}
                />
            )}

            {isProfileDialogOpen && (
                <ProfileDialog
                    employeeInfo={managerInfo}
                    onClose={handleCloseProfileDialog}
                    onUpdate={(updatedData) => setManagerInfo(prev => ({ ...prev, ...updatedData }))}
                />
            )}
        </div>
    );
};

export default MainManager;
